import { useEffect } from 'react';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Spinner,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useCustomerContacts, type ContactOption } from './use-customer-contacts';

// Stable empty list: a fresh `[]` per render re-fires the roles effect below
// and loops (parent state → re-render → new array → effect).
const NO_CONTACTS: ContactOption[] = [];

/**
 * Signatory contact picker for the contract-create form. Loads the selected
 * customer's contact options (`/customers/:id/contacts/options`) and, on change,
 * bubbles up the picked option's `contact_roles` so the parent can gate the
 * `supplier` contract role. Disabled until a customer is chosen.
 */
export function ContactSelect({
  customerId,
  value,
  onChange,
  onRolesChange,
}: {
  customerId: number;
  value: number;
  onChange: (id: number) => void;
  onRolesChange: (roles: string[]) => void;
}) {
  const { data, isFetching } = useCustomerContacts(customerId);
  const options: ContactOption[] = data ?? NO_CONTACTS;

  // Keep the parent's role gate in sync with the current selection / option set.
  useEffect(() => {
    const picked = options.find((o) => o.value === value);
    onRolesChange(picked?.contact_roles ?? []);
  }, [options, value, onRolesChange]);

  const disabled = !customerId;

  return (
    <Field
      label="Contact"
      description={disabled ? 'Select a customer first.' : undefined}
    >
      {(p) => (
        <Select
          value={value > 0 ? String(value) : ''}
          onValueChange={(v) => onChange(Number(v))}
          disabled={disabled}
        >
          <SelectTrigger {...p} disabled={disabled}>
            <SelectValue
              placeholder={
                isFetching ? 'Loading contacts…' : disabled ? 'No customer selected' : 'Select a contact'
              }
            />
            {isFetching ? <Spinner size="sm" /> : null}
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={String(o.value)}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  );
}
